import * as React from 'react';
import { Box, PaletteColor, Typography, useMediaQuery, useTheme } from '@mui/material';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import TimelineHaloWithDot from './TimelineHaloWithDot';
import {
    type TimelineColorContext,
    getHaloColor,
    getDotColor,
    getConnectorColor,
    getDesktopAlignment,
} from './timelineColors';

export type TimelineStepProps = {
    label: string;
    icon?: IconName;
    current: boolean;
    id: number;
    onClick?: () => void;
};

export type TimelineProps = {
    steps: Array<TimelineStepProps>;
    color?: PaletteColor;
};

// Sizing constants — change once to resize dots/connectors everywhere
const HALO_SIZE = 20;
const DOT_SIZE = 12;
const CONNECTOR_THICKNESS = 2;

export const Timeline: React.FunctionComponent<TimelineProps> = function (props: TimelineProps) {
    const { steps, color } = props;
    const { palette, breakpoints } = useTheme();
    const isMobile = useMediaQuery(breakpoints.down('md'));
    const mainColor = color || palette.primary;

    let currentDone = false;
    const currentIndex = steps.reduce((accumulator, step) => {
        currentDone = currentDone || step.current;
        return !currentDone || step.current ? accumulator + 1 : accumulator;
    }, -1);

    // ── Color context (passed to pure helper functions) ──────────────────────

    const inactiveColor = palette.mode === 'dark' ? palette.grey[600] : palette.grey[300];

    const colorCtx: TimelineColorContext = {
        isDark: palette.mode === 'dark',
        primaryMain: mainColor.main,
        greyLight: palette.grey[200],
        greyDark: palette.grey[700],
        inactiveColor,
    };

    // ── Sx groups ────────────────────────────────────────────────────────────

    // Outer container
    const wrapperSx = {
        width: '100%',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
    } as const;

    // Desktop step: flex column, equal width, content aligned by position
    const desktopStepSx = (index: number, clickable: boolean) => ({
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: getDesktopAlignment(index, steps.length),
        flexGrow: 1,
        gap: '8px',
        cursor: clickable ? 'pointer' : 'auto',
        color: palette.grey[500],
        '&:hover': { color: clickable ? mainColor.main : palette.grey[500] },
    });

    // Desktop dot row: [left half-connector] [halo] [right half-connector]
    const dotRowSx = {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
    };

    // Horizontal connector half
    const horizontalConnectorSx = (connectorColor: string) => ({
        flex: 1,
        height: `${CONNECTOR_THICKNESS}px`,
        backgroundColor: connectorColor,
        borderRadius: '1px',
    });

    // Mobile step: full-width row, connector column on the left
    const mobileStepSx = (clickable: boolean) => ({
        display: 'flex',
        flexDirection: 'row' as const,
        alignItems: 'stretch',
        width: '100%',
        cursor: clickable ? 'pointer' : 'auto',
        color: palette.grey[500],
        '&:hover': { color: clickable ? mainColor.main : palette.grey[500] },
    });

    // Mobile connector column: [top segment] [halo] [bottom segment]
    const mobileConnectorColumnSx = {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        width: `${HALO_SIZE}px`,
    };

    // Vertical connector segment
    const verticalConnectorSx = (connectorColor: string, visible: boolean) => ({
        flex: 1,
        width: `${CONNECTOR_THICKNESS}px`,
        minHeight: '12px',
        backgroundColor: connectorColor,
        borderRadius: '1px',
        visibility: visible ? ('visible' as const) : ('hidden' as const),
    });

    // Step label
    const labelSx = (clickable: boolean) => ({
        fontWeight: '500',
        fontSize: isMobile ? '14px' : '12px',
        lineHeight: '14.4px',
        cursor: clickable ? 'pointer' : 'auto',
        transition: 'color 0.225s ease-out',
    });

    // ── Render helper ────────────────────────────────────────────────────────

    const renderHalo = (index: number, icon?: IconName) => (
        <TimelineHaloWithDot
            haloColor={getHaloColor(index, currentIndex, colorCtx)}
            dotColor={getDotColor(index, currentIndex, colorCtx)}
            iconColor={palette.background.paper}
            icon={icon}
            haloSize={HALO_SIZE}
            dotSize={DOT_SIZE}
        />
    );

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <Box
            sx={wrapperSx}
            data-testid="timeline"
        >
            {steps.map((step, index) => {
                const isFirst = index === 0;
                const isLast = index === steps.length - 1;
                const clickable = !!step.onClick;

                // Left segment = done when the current step is here or past it
                const leftColor = getConnectorColor(currentIndex >= index, colorCtx);
                // Right segment = done only when this step is already behind us
                const rightColor = getConnectorColor(currentIndex > index, colorCtx);

                if (isMobile) {
                    return (
                        <Box
                            key={`timeline-step-${step.label}`}
                            sx={mobileStepSx(clickable)}
                            onClick={step.onClick}
                        >
                            {/* Left column: top line → halo+dot → bottom line */}
                            <Box sx={mobileConnectorColumnSx}>
                                <Box sx={verticalConnectorSx(leftColor, !isFirst)} />
                                {renderHalo(index, step.icon)}
                                <Box sx={verticalConnectorSx(rightColor, !isLast)} />
                            </Box>

                            {/* Label */}
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    ml: 1.5,
                                }}
                            >
                                <Typography
                                    component="span"
                                    sx={labelSx(clickable)}
                                >
                                    {step.label}
                                </Typography>
                            </Box>
                        </Box>
                    );
                }

                return (
                    <Box
                        key={`timeline-step-${step.label}`}
                        sx={desktopStepSx(index, clickable)}
                        onClick={step.onClick}
                    >
                        {/* Dot row: [left half] [halo+dot] [right half] */}
                        <Box sx={dotRowSx}>
                            {!isFirst && <Box sx={horizontalConnectorSx(leftColor)} />}
                            {renderHalo(index, step.icon)}
                            {!isLast && <Box sx={horizontalConnectorSx(rightColor)} />}
                        </Box>

                        {/* Label */}
                        <Typography
                            component="span"
                            sx={labelSx(clickable)}
                        >
                            {step.label}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
};
